import * as XLSX from 'xlsx'
import type { DashboardOverview } from '@/api/dashboard'
import {
  buildDashboardReport,
  buildReportFileName,
  sanitizeSheetName,
  type ReportSheet,
  type ReportSummaryInput,
} from './dashboardReport'

/**
 * 工作台报表导出（纯前端）。
 *
 * 为什么不下发到后端：工作台的数据本身就是**聚合结果**（趋势 7 个点、门店排行 4 条、
 * 热销 3 条），导出量很小；而后端 `DashboardController` 只有 `/dashboard/overview`
 * 一个接口（全仓无任何导出接口），为「导出当前页」在建后端接口不划算。
 *
 * 注意边界：导出的就是**用户当前在页面上看到的那份数据**。如果将来需要
 * 「按日期区间导出全部订单明细」，那必须走后端新增接口，不能在前端拼。
 */

function toWorksheet(sheet: ReportSheet): XLSX.WorkSheet {
  const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows)
  // 列宽按内容最长者估算，让导出的表格不用手工拉宽就能看
  const widths = sheet.rows.reduce<number[]>((acc, row) => {
    row.forEach((cell, index) => {
      // 中文按 2 个字符宽度估算
      const text = String(cell ?? '')
      const width = text.length + (text.match(/[\u4e00-\u9fa5]/g)?.length ?? 0)
      acc[index] = Math.max(acc[index] ?? 8, Math.min(width + 2, 40))
    })
    return acc
  }, [])
  worksheet['!cols'] = widths.map((width) => ({ wch: width }))
  return worksheet
}

/** 把若干 sheet 组装成工作簿 */
export function buildWorkbook(report: ReportSheet[]): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new()
  report.forEach((sheet) => {
    XLSX.utils.book_append_sheet(workbook, toWorksheet(sheet), sanitizeSheetName(sheet.name))
  })
  return workbook
}

/** 写文件的落点。默认走 `XLSX.writeFile`（浏览器里即触发下载），测试可注入替身 */
export type WorkbookWriter = (workbook: XLSX.WorkBook, fileName: string) => void

const defaultWriter: WorkbookWriter = (workbook, fileName) => {
  XLSX.writeFile(workbook, fileName)
}

/**
 * 导出工作台报表并触发浏览器下载。
 *
 * `writer` 默认是 `XLSX.writeFile`；测试注入替身来断言「写了什么」，
 * 因为 ESM 下 `xlsx` 的导出属性不可重定义（`vi.spyOn` 会报
 * "Module namespace is not configurable"），只能靠依赖注入。
 *
 * @returns 下载的文件名，便于调用方给出提示或写测试断言
 */
export function exportDashboardReport(
  overview: DashboardOverview,
  summary: ReportSummaryInput,
  writer: WorkbookWriter = defaultWriter,
): string {
  const fileName = buildReportFileName(summary.generatedAt)
  writer(buildWorkbook(buildDashboardReport(overview, summary)), fileName)
  return fileName
}
